import { Monitor, Moon, Sun } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { type Theme, useTheme } from "~/hooks/useTheme";

const OPTIONS: { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

interface ThemeToggleProps {
  /** Tailwind classes for the trigger button. Caller is responsible for
   *  sizing and color so the toggle can sit naturally in both desktop
   *  nav and the mobile menu without a parent wrapper deciding it. */
  className?: string;
  /** Whether to render the "Theme" label inside the dropdown. */
  showLabel?: boolean;
}

export default function ThemeToggle({
  className,
  showLabel = false,
}: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  // Pick the icon that represents the *currently applied* (resolved) theme,
  // not the stored preference — that way the button shows what the user
  // actually sees right now, including the "system" -> OS-driven state.
  // resolveTheme lives in the hook module; we re-derive here for the icon.
  const triggerIcon =
    theme === "light"
      ? Sun
      : theme === "dark"
        ? Moon
        : Monitor;

  const TriggerIcon = triggerIcon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Toggle theme"
          title="Toggle theme"
          className={
            className ??
            "h-9 w-9 text-text-secondary hover:text-text-primary hover:bg-bg-elevated"
          }
        >
          <TriggerIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        // z-[60] sits above the sticky navbar (z-50) and below the mobile
        // menu overlay (z-100). For the mobile-menu trigger we close the
        // overlay on open so the user is never looking through a z-100 pane.
        className="z-[60] min-w-[10rem]"
      >
        {showLabel && (
          <>
            <DropdownMenuLabel className="text-text-secondary font-normal text-xs">
              Theme
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => setTheme(value as Theme)}
        >
          {OPTIONS.map(({ value, label, Icon }) => (
            <DropdownMenuRadioItem
              key={value}
              value={value}
              className="cursor-pointer"
            >
              <Icon className="size-4" />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
